import { createContext, useContext, useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";

type CoachModalSize = "default" | "wide" | "workspace" | "sheet";

const CoachModalDepth = createContext(0);
/** Portal DOM order can put a newly mounted parent after its child on a route
 * return. React ancestry owns layers; keyboard/focus follows the visible layer. */
function topActiveModal(): Element | undefined {
  return Array.from(document.querySelectorAll("[data-coach-modal-active='true']"))
    .reduce<Element | undefined>((top, element) => !top || Number(getComputedStyle(element).zIndex) >= Number(getComputedStyle(top).zIndex) ? element : top, undefined);
}

const activeModalLocks = new Set<object>();
let unlockedBodyOverflow = "";

const SIZE_CLASS: Record<CoachModalSize, string> = {
  sheet: "h-[100dvh] max-h-[100dvh] max-w-[40rem]",
  default: "max-w-lg sm:max-h-[90dvh]",
  wide: "max-w-5xl sm:max-h-[92dvh]",
  workspace: "max-w-[90rem] sm:h-[min(92dvh,60rem)] sm:max-h-[92dvh]",
};

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

/**
 * Portal shell for coach form and workspace dialogs.
 *
 * The default presentation intentionally preserves the compact shape used by
 * existing list-page modals. Wide and workspace sizing are opt-in so a dense
 * authoring flow can use the available desktop canvas without changing every
 * CoachModal caller.
 */
export type CoachModalProps = {
  /** Retain owned form state while disclosure is closed. Inactive dialogs own no focus/scroll lock. */
  active?: boolean;
  /** Heading text — also the dialog's accessible name. */
  title: string;
  closeLabel?: string;
  /** Optional supporting copy announced with the title. */
  description?: string;
  /** Opt-in desktop width/height treatment. Defaults to the legacy size. */
  size?: CoachModalSize;
  /** Controls displayed beside the heading, before the close button. */
  headerActions?: ReactNode;
  /** Persistent action region below the independently scrolling body. */
  footer?: ReactNode;
  /** Optional classes for the persistent footer. */
  footerClassName?: string;
  /** Optional classes for the scrolling body. */
  bodyClassName?: string;
  /** Close handler — backdrop click, Escape, or the close button. */
  onClose: () => void;
  /** Explicit focus return target for modal-to-modal hand-offs. */
  returnFocusTo?: HTMLElement | null;
  children: ReactNode;
  /** Optional testid on the dialog element. */
  "data-testid"?: string;
};

export function CoachModal({
  active = true,
  title,
  closeLabel = "Close",
  description,
  size = "default",
  headerActions,
  footer,
  footerClassName,
  bodyClassName,
  onClose,
  returnFocusTo,
  children,
  ...rest
}: CoachModalProps) {
  const depth = useContext(CoachModalDepth);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const capturedReturnFocus = useRef<HTMLElement | null>(
    typeof document !== "undefined" &&
      document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null,
  );
  const titleId = useId();
  const descriptionId = useId();

  // Keep the page behind the dialog stationary, move keyboard focus into the
  // portal, and return it to the opening control when this modal unmounts.
  useEffect(() => {
    if (!active) return;
    // A retained child may be opened from a different row after ordinary Close.
    // Capture the current invoker on each disclosure, not only its first mount.
    if (!returnFocusTo && document.activeElement instanceof HTMLElement &&
      !panelRef.current?.contains(document.activeElement))
      capturedReturnFocus.current = document.activeElement;
    const lock = {};
    if (activeModalLocks.size === 0) unlockedBodyOverflow = document.body.style.overflow;
    activeModalLocks.add(lock);
    document.body.style.overflow = "hidden";

    const frame = window.requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel || topActiveModal() !== panel.parentElement || panel.contains(document.activeElement)) return;
      const preferred = panel.querySelector<HTMLElement>("[autofocus]");
      const first = panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (preferred ?? first ?? panel).focus();
    });

    return () => {
      window.cancelAnimationFrame(frame);
      activeModalLocks.delete(lock);
      if (activeModalLocks.size === 0) document.body.style.overflow = unlockedBodyOverflow;
      try {
        (returnFocusTo ?? capturedReturnFocus.current)?.focus();
      } catch {
        // The trigger may have been removed as part of a modal hand-off.
      }
    };
  }, [active, returnFocusTo]);

  // Escape closes unless a ConfirmDialog or one of this form's portaled
  // pickers is layered above the dialog. Tab containment includes those
  // owned pickers so keyboard focus never falls through to the page.
  useEffect(() => {
    if (!active) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      const wrapper = panelRef.current?.parentElement;
      if (topActiveModal() !== wrapper) return;
      if (document.querySelector("[data-confirm-dialog]")) return;
      if (event.key === "Escape") {
        if (event.defaultPrevented) return;
        if (document.querySelector("[data-tktic-popover]")) return;
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;

      const panel = panelRef.current;
      if (!panel) return;
      const roots: HTMLElement[] = [
        panel,
        ...Array.from(
          document.querySelectorAll<HTMLElement>("[data-tktic-popover]"),
        ),
      ];
      const focusable = roots
        .flatMap((root) =>
          Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)),
        )
        .filter((element) => {
          const style = window.getComputedStyle(element);
          return style.display !== "none" && style.visibility !== "hidden";
        });

      if (focusable.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const activeIsOwned = roots.some((root) => root.contains(active));
      if (!activeIsOwned) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <CoachModalDepth.Provider value={depth + 1}><div
      className={cn(
        "fixed inset-0 z-[130] flex bg-black/80",
        size === "sheet" ? "items-stretch justify-end p-0" : "items-end justify-center sm:items-center sm:p-4",
        size === "default" ? "p-3" : "p-0",
      )}
      role="dialog"
      aria-modal={active ? true : undefined}
      aria-hidden={!active || undefined}
      style={{ zIndex: 130 + depth * 10, ...(!active ? { display: "none" } : {}) }}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      data-testid={rest["data-testid"]}
      data-coach-modal="true"
      data-coach-modal-active={active}
      data-modal-size={size}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        data-testid="coach-modal-panel"
        className={cn(
          "flex w-full min-w-0 flex-col overflow-hidden border border-rule bg-surface shadow-overlay",
          size === "sheet"
            ? "rounded-none border-y-0 border-r-0"
            : size === "default"
            ? "max-h-[calc(100dvh-1.5rem)] rounded"
            : "max-h-[calc(100dvh-0.75rem)] rounded-t-control sm:rounded-panel",
          SIZE_CLASS[size],
        )}
      >
        <header
          role="none"
          className="flex shrink-0 items-start justify-between gap-4 border-b border-rule bg-elevated px-4 py-3 sm:px-5 sm:py-4"
        >
          <div className="min-w-0">
            <h2
              id={titleId}
              className="text-lg font-display tracking-tight text-ink sm:text-xl"
            >
              {title}
            </h2>
            {description ? (
              <p
                id={descriptionId}
                className="mt-1 max-w-3xl text-sm text-ink-muted"
              >
                {description}
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {headerActions}
            <button
              type="button"
              onClick={onClose}
              data-testid="coach-modal-close"
              aria-label={closeLabel}
              title="Close"
              className="inline-flex size-11 items-center justify-center rounded-control text-ink-muted transition-colors hover:bg-field hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <X aria-hidden="true" className="size-5" />
            </button>
          </div>
        </header>

        <div
          className={cn(
            "min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-4 pt-4 sm:px-5 sm:pt-5",
            footer
              ? "pb-4 sm:pb-5"
              : "pb-[calc(1rem+env(safe-area-inset-bottom))] sm:pb-5",
            bodyClassName,
          )}
          data-testid="coach-modal-body"
        >
          {children}
        </div>

        {footer ? (
          <footer
            role="none"
            className={cn(
              "shrink-0 border-t border-rule bg-surface px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:px-5 sm:py-3",
              footerClassName,
            )}
            data-testid="coach-modal-footer"
          >
            {footer}
          </footer>
        ) : null}
      </div>
    </div></CoachModalDepth.Provider>,
    document.body,
  );
}
